import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, MessageCircle, MessageSquare, MapPin, Clock } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const contactMethods = [
  {
    icon: Phone,
    title: 'Anrufen',
    description: '+49 175 6900374',
    href: 'tel:+491756900374',
    label: 'Direkt anrufen'
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    description: '+49 175 6900374',
    href: 'https://wa.me/491756900374?text=Guten%20Tag%20Herr%20Dogan%2C%20ich%20interessiere%20mich%20f%C3%BCr%20eine%20Immobilienberatung.',
    label: 'Chat starten'
  },
  {
    icon: MessageSquare,
    title: 'SMS',
    description: '+49 175 6900374',
    href: 'sms:+491756900374',
    label: 'SMS senden'
  },
  {
    icon: Mail,
    title: 'E-Mail',
    description: 'info@immobilien-dogan.com',
    href: 'mailto:info@immobilien-dogan.com',
    label: 'E-Mail schreiben'
  }
];

export default function Kontakt() {
  return (
    <div data-testid="kontakt-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Kontakt</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              KONTAKT
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Sprechen Sie direkt mit Serdar Dogan
            </h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Ob telefonisch, per WhatsApp oder E-Mail – ich bin für Sie da. 
              Wählen Sie Ihren bevorzugten Kommunikationsweg.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 border-b border-[#d1af6a]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => (
              <motion.a
                key={method.title}
                href={method.href}
                target={method.href.startsWith('http') ? '_blank' : undefined}
                rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-luxury p-6 text-center group"
                data-testid={`contact-method-${method.title.toLowerCase()}`}
              >
                <div className="w-14 h-14 bg-[#d1af6a]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#d1af6a]/20 transition-colors">
                  <method.icon className="w-7 h-7 text-[#d1af6a]" />
                </div>
                <h3 className="font-subheading text-lg text-white mb-2">
                  {method.title}
                </h3>
                <p className="text-[#c8c6c4] text-sm mb-4">
                  {method.description}
                </p>
                <span className="text-[#d1af6a] text-sm font-medium group-hover:underline">
                  {method.label}
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl sm:text-3xl text-white mb-8">
                Schreiben Sie mir
              </h2>
              <ContactForm />
            </div>

            {/* Info */}
            <div className="space-y-8">
              <div className="card-luxury p-6">
                <h3 className="font-subheading text-lg text-white mb-4">
                  Kontaktdaten
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-[#d1af6a] mt-0.5" />
                    <div>
                      <p className="text-[#c8c6c4] text-sm">Telefon</p>
                      <a href="tel:+491756900374" className="text-white hover:text-[#d1af6a]">
                        +49 175 6900374
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[#d1af6a] mt-0.5" />
                    <div>
                      <p className="text-[#c8c6c4] text-sm">E-Mail</p>
                      <a href="mailto:info@immobilien-dogan.com" className="text-white hover:text-[#d1af6a]">
                        info@immobilien-dogan.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#d1af6a] mt-0.5" />
                    <div>
                      <p className="text-[#c8c6c4] text-sm">Region</p>
                      <p className="text-white">Rhein-Main-Gebiet</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-luxury p-6">
                <h3 className="font-subheading text-lg text-white mb-4">
                  Erreichbarkeit
                </h3>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#d1af6a] mt-0.5" />
                  <div className="text-[#c8c6c4] text-sm space-y-1">
                    <p>Montag – Freitag: 9:00 – 18:00</p>
                    <p>Samstag: Nach Vereinbarung</p>
                    <p>Sonntag: Geschlossen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
